import { useState, useCallback } from "react";

/**
 * Hook for resizable table columns.
 * @param {number[]} initialWidths - Initial width in px for each column
 * @param {number} minWidth - Minimum column width in px
 * @returns {{ columnWidths: number[], getColumnStyle: (index: number) => object, handleResizeStart: (index: number, e: MouseEvent) => void }}
 */
export function useResizableColumns(initialWidths, minWidth = 60) {
  const [columnWidths, setColumnWidths] = useState(initialWidths);

  const getColumnStyle = useCallback(
    (index) => ({
      width: columnWidths[index],
      minWidth: columnWidths[index],
      maxWidth: columnWidths[index],
    }),
    [columnWidths]
  );

  const handleResizeStart = useCallback(
    (colIndex, e) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = columnWidths[colIndex];

      const handleMouseMove = (moveEvent) => {
        const delta = moveEvent.clientX - startX;
        setColumnWidths((prev) => {
          const next = [...prev];
          next[colIndex] = Math.max(minWidth, startWidth + delta);
          return next;
        });
      };

      const handleMouseUp = () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [columnWidths, minWidth]
  );

  return { columnWidths, getColumnStyle, handleResizeStart };
}
